import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SCUIContent } from '../../atomic/organisms';
import { Box, Card, CardContent, Grid, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { PaymentConsolidationDataGrid } from './components/PaymentConsolidationDataGrid';
import { PaymentConsolidationSearchSummary } from './components/PaymentConsolidationSearchSummary';
import OrderQuery from '../../services/query/order/OrderQuery.services';
import { SCUIButton, SCUILoadingLinear } from '../../atomic/atoms';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import CopyToClipboardButton from '../../atomic/atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { PC } from './paymentConsolidation.classes';
import {
    extractNotFoundIds,
    extractSearchOrdersByIdentifiersResults,
    extractSearchSummary,
    extractUseSpForBulkShipmentSearch,
    mapSearchOrdersByIdentifiersItem,
    resolveScrTranslatedLabel,
    type SearchOrdersSummary,
} from './paymentConsolidation.mapper';

export default function PaymentConsolidation() {
    const { t } = useTranslation();
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(100);
    const [rows, setRows] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState<number>(0);
    const [shippingNumbers, setShippingNumbers] = useState('');
    const [excelProps, setExcelProps] = useState('');
    const [loading, setLoading] = useState<boolean>(false);
    const [allRows, setAllRows] = useState<any[]>([]);
    const [notFoundIds, setNotFoundIds] = useState<any[]>([]);
    const [searchSummary, setSearchSummary] = useState<SearchOrdersSummary | null>(null);
    const searchCardRef = useRef<HTMLDivElement>(null);
    const [searchPanelHeight, setSearchPanelHeight] = useState<number | undefined>();

    useLayoutEffect(() => {
        const searchCardElement = searchCardRef.current;
        if (!searchCardElement) {
            return;
        }

        const updatePanelHeight = () => {
            setSearchPanelHeight(searchCardElement.offsetHeight);
        };

        updatePanelHeight();

        const resizeObserver = new ResizeObserver(updatePanelHeight);
        resizeObserver.observe(searchCardElement);

        return () => resizeObserver.disconnect();
    }, [searchSummary, notFoundIds.length, shippingNumbers]);

    useEffect(() => {
        if (allRows.length > 0) {
            updatePageRows(allRows, pageNumber, pageSize);
        }
    }, [pageNumber, pageSize, allRows]);

    useEffect(() => {
        BuildExcelData();
    }, [allRows]);

    const updatePageRows = (allRows: any[], pageNumber: number, pageSize: number) => {
        const startIndex = pageNumber * pageSize;
        const endIndex = startIndex + pageSize;
        setRows(allRows.slice(startIndex, endIndex));
    };
    const BuildExcelData = () => {
        const excelColumnOrder: Array<{ key: string; label: string }> = [
            { key: 'orderNumber', label: t('general.orderNumber') },
            { key: 'shippingListNo', label: t('general.shippingNo') },
            { key: 'orderDateStr', label: t('general.orderDate') },
            { key: 'shippedDateStr', label: t('general.shippedDate') },
            { key: 'deliveredDateStr', label: t('paymentConsolidation.deliveredDate') },
            { key: 'lastUpdatedDateStr', label: t('paymentConsolidation.lastUpdatedDate') },
            { key: 'cargoCompanyName', label: t('general.cargoCompanyName') },
            { key: 'trackingCode', label: t('paymentConsolidation.trackingCode') },
            { key: 'shippingListStatusName', label: t('general.shippingListStatusName') },
            { key: 'cityName', label: t('general.cityName') },
            { key: 'countyName', label: t('general.countyName') },
            { key: 'neighborhoodName', label: t('general.neighborhoodName') },
            { key: 'deliveryMethodName', label: t('paymentConsolidation.deliveryMethodDistribution') },
            { key: 'storeCode', label: t('general.storeCode') },
            { key: 'storeName', label: t('paymentConsolidation.storeName') },
            { key: 'dPackageCargoShipmentCode', label: t('paymentConsolidation.dPackageCargoShipmentCode') },
            { key: 'shipmentOwner', label: t('paymentConsolidation.orderSource') },
            { key: 'platform', label: t('paymentConsolidation.platform') },
            { key: 'packageCount', label: t('paymentConsolidation.packageCount') },
            { key: 'paymentAmount', label: t('orderDetail.paymentAmount') },
            { key: 'paymentType', label: t('general.paymentType') },
            { key: 'scr', label: t('customerInformation.scr') },
        ];

        const parseData: any = allRows.map((row) => {
            const mappedRow: Record<string, string | number> = {};
            excelColumnOrder.forEach(({ key, label }) => {
                if (key === 'paymentType') {
                    mappedRow[label] = row.paymentMethodName ?? '-';
                    return;
                }

                if (key === 'scr') {
                    mappedRow[label] = resolveScrTranslatedLabel(row.customerId, row.isScr, {
                        yes: t('general.yes'),
                        no: t('general.no'),
                    });
                    return;
                }

                mappedRow[label] = row[key] ?? '-';
            });

            return mappedRow;
        });

        setExcelProps(parseData);
    };

    const GetPaymentConsolidation = useCallback(
        async (shippingNumbers: string) => {
            setLoading(true);

            try {
                const identifierList = shippingNumbers
                    .split(/[\n,]/)
                    .map((identifier: string) => identifier.trim())
                    .filter((identifier: string) => identifier.length > 0);

                const req = { SearchIdentifiers: identifierList.join(',') };

                const response = await OrderQuery.SearchOrdersByIdentifiers(req);
                const useSp = extractUseSpForBulkShipmentSearch(response);
                const allResults = extractSearchOrdersByIdentifiersResults(response).map((item) =>
                    mapSearchOrdersByIdentifiersItem(item, useSp)
                );

                setAllRows(allResults);
                setNotFoundIds(extractNotFoundIds(response));
                setSearchSummary(extractSearchSummary(response));
                setRowCount(allResults.length);
                updatePageRows(allResults, pageNumber, pageSize);
            } catch (error) {
                console.error('Error during SearchOrdersByIdentifiers:', error);
            } finally {
                setLoading(false);
            }
        },
        [pageNumber, pageSize]
    );

    return (
        <SCUIContent className={PC.page}>
            <Grid>
                <div className={PC.breadcrumbSection}>
                    <SCUIBreadCrumb
                        title={t('paymentConsolidation.pageTitle')}
                        componentLink={'/payment-consolidation'}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
                <Grid
                    className={PC.loadingSection}
                    size={{
                        xs: 12,
                        md: 12,
                        lg: 12,
                        xl: 12,
                    }}
                >
                    {loading && <SCUILoadingLinear display={loading} />}
                </Grid>
                <Box
                    className={PC.mainLayout}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        gap: 2,
                        pt: 2,
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <Box className={PC.inputColumn} sx={{ width: { xs: '100%', sm: 260 }, flexShrink: 0 }}>
                        <Card ref={searchCardRef} className={PC.searchCard} variant="outlined" sx={{ width: '100%' }}>
                            <CardContent className={PC.searchCardContent} sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <TextField
                                    className={PC.inputTextShippingNumbers}
                                    label={t('paymentConsolidation.searchIdentifiersLabel') as string}
                                    multiline={true}
                                    rows={8}
                                    onChange={(event: any) => {
                                        setShippingNumbers(event.target.value);
                                    }}
                                    value={shippingNumbers}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ marginBottom: 1, width: '100%' }}
                                />
                                <Box className={PC.actionsRow} sx={{ display: 'flex', gap: 2 }}>
                                    <Box className={PC.clearButtonWrap} sx={{ position: 'relative', zIndex: 3 }}>
                                        <SCUIButton
                                            className={PC.inputButtonClear}
                                            text={t('general.clear')}
                                            type="error"
                                            buttonType="submit"
                                            sizeType="default"
                                            size="m"
                                            style={{ width: 110, borderRadius: '4px' }}
                                            startIcon={<DeleteIcon style={{ color: '#ffffff' }} />}
                                            onClick={() => {
                                                setShippingNumbers('');
                                                setAllRows([]);
                                                setRows([]);
                                                setNotFoundIds([]);
                                                setSearchSummary(null);
                                                setRowCount(0);
                                            }}
                                        />
                                    </Box>
                                    <Box className={PC.searchButtonWrap} sx={{ position: 'relative', zIndex: 2 }}>
                                        <SCUIButton
                                            className={PC.inputButtonSearch}
                                            text={t('general.search')}
                                            type="primary"
                                            buttonType="submit"
                                            sizeType="default"
                                            size="m"
                                            style={{ width: 110, borderRadius: '4px' }}
                                            startIcon={<SearchIcon style={{ color: '#ffffff' }} />}
                                            onClick={() => GetPaymentConsolidation(shippingNumbers)}
                                        />
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                    {searchSummary && (
                        <Box
                            className={PC.summaryColumn}
                            sx={{
                                width: { xs: '100%', sm: 220 },
                                flexShrink: 0,
                                height: searchPanelHeight,
                            }}
                        >
                            <PaymentConsolidationSearchSummary summary={searchSummary} />
                        </Box>
                    )}
                    {notFoundIds.length > 0 && (
                        <Box
                            className={PC.resultsColumn}
                            sx={{
                                width: { xs: '100%', sm: 220 },
                                flexShrink: 0,
                                height: searchPanelHeight,
                            }}
                        >
                            <Card
                                className={PC.resultsCard}
                                variant="outlined"
                                sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}
                            >
                                <CardContent
                                    className={PC.resultsCardContent}
                                    sx={{
                                        p: 2,
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        minHeight: 0,
                                        '&:last-child': { pb: 2 },
                                    }}
                                >
                                    <Typography
                                        className={PC.resultsTitle}
                                        variant="subtitle1"
                                        color="error"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            mb: 1,
                                            fontWeight: 600,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {t('goalStatusSending.notFoundData') as string}
                                        <CopyToClipboardButton
                                            className={PC.copyResultsButton}
                                            textToCopy={notFoundIds.filter((val) => val !== undefined).join('\n')}
                                        />
                                    </Typography>

                                    <Box
                                        className={PC.resultsListWrap}
                                        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', width: '100%' }}
                                    >
                                        <List className={PC.resultsList} dense disablePadding>
                                            {notFoundIds.map((val: any, index: number) => (
                                                <ListItem className={PC.resultsListItem} key={index} disablePadding>
                                                    <ListItemText primary={`${val}`} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </Box>
                {rows && rows.length > 0 && (
                    <Grid
                        size={{
                            xs: 12,
                            lg: 12,
                            md: 12,
                            xl: 12,
                        }}
                        sx={{ width: '100%', minWidth: 0 }}
                    >
                        <PaymentConsolidationDataGrid
                            props={{
                                GetPaymentConsolidation: GetPaymentConsolidation,
                                pageNumber: pageNumber,
                                pageSize: pageSize,
                                rows: rows,
                                rowCount: rowCount,
                                setPageNumber: setPageNumber,
                                setPageSize: setPageSize,
                                setRows: setRows,
                                setRowCount: setRowCount,
                                excelProps: excelProps,
                                hiddenExcelColumn: [
                                    'Sevkiyat ID',
                                    'Sipariş ID',
                                    'Ödeme Yöntemi ID',
                                    'Müşteri ID',
                                    'Ödeme ID',
                                    'Taksit Sayısı',
                                    'Kredi Kartı Tipi ID',
                                ],
                            }}
                        />
                    </Grid>
                )}
            </Grid>
        </SCUIContent>
    );
}
